import { useState, useEffect } from "react";
import Select from "react-select";
import api from "../Axios/api";
const OpeningStock = () => {
  // Dropdown options
  const [facLoc, setFacLoc] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);

  // Selected filters
  const [selectedFactory, setSelectedFactory] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Table data
  const [tableItems, setTableItems] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Error and success messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // --------------------------------------------------
  // Fetch factory locations
  // --------------------------------------------------
  const fetchFactoryLocations = async () => {
    try {
      const res = await api.get(
        "/factory-location/get-factory-location"
      );

      const newData = res.data.map((location) => ({
        value: location._id,
        label: location.fectory_loca_name,
        inventoryStartDate: location.inventory_start_date,
      }));

      setFacLoc(newData);
    } catch (error) {
      console.error("Error fetching factory locations:", error);
      setError("Failed to fetch factory locations.");
    }
  };

  // --------------------------------------------------
  // Fetch item categories
  // --------------------------------------------------
  const fetchItemGroups = async () => {
    try {
      const res = await api.get(
        "/purchase-item-category/get-name"
      );

      const newData = res.data.map((cat) => ({
        value: cat._id,
        label: cat.itemcat_title,
      }));

      setItemGroups(newData);
    } catch (error) {
      console.error("Error fetching item categories:", error);
      setError("Failed to fetch item categories.");
    }
  };

  // --------------------------------------------------
  // Initial API calls
  // --------------------------------------------------
  useEffect(() => {
    fetchFactoryLocations();
    fetchItemGroups();
  }, []);

  // --------------------------------------------------
  // Clear table when filters change
  // --------------------------------------------------
  useEffect(() => {
    setTableItems([]);
    setError("");
    setSuccess("");
  }, [selectedFactory, selectedGroup]);

  // --------------------------------------------------
  // Fetch items based on factory and category
  // --------------------------------------------------
const handleSearch = async () => {
    if (!selectedFactory) {
        setError("Please select a factory location.");
        return;
    }

    if (!selectedGroup) {
        setError("Please select an item category.");
        return;
    }

    try {
        setLoading(true);
        setError("");
        setSuccess("");
        setTableItems([]);

        const factoryId = selectedFactory.value;
        const catId = selectedGroup.value;

        // Fetch master items and existing opening stock
        const [itemRes, openingRes] = await Promise.all([
            api.get("/purchase-item/get-item-by-catloc/", {
                params: {
                    catId,
                    factory: factoryId,
                },
            }),
            api.get("/inventory/opening"),
        ]);
        const allItems = itemRes.data;
        const openingData = openingRes.data.data || [];

        // Match items with existing opening stock
        const items = allItems.map((item) => {
            const savedStock = openingData.find(
                (stock) =>
                    String(stock.itemId) === String(item._id) &&
                    String(stock.factoryId) === String(factoryId)
            );

            return {
                itemId: item._id,
                itemName: item.item_title,
                unit: item.item_unit,

                // Populate saved values if opening stock exists
                quantity: savedStock ? String(savedStock.quantity) : "",
                rate: savedStock ? String(savedStock.rate) : "",
                amount: savedStock ? savedStock.amount : 0,

                // Lock previously submitted items
                hasStock: Boolean(savedStock),
                selected: false,
            };
        });

        setTableItems(items);

        if (items.length === 0) {
            setError("No items found for this factory and category.");
        }
    } catch (error) {
        console.error("Error fetching opening stock items:", error);

        setError(
            error.response?.data?.message ||
            "Failed to fetch items. Please try again."
        );
    } finally {
        setLoading(false);
    }
};

  // --------------------------------------------------
  // Handle individual checkbox
  // --------------------------------------------------
  const handleCheckboxChange = (itemId) => {
    setTableItems((prevItems) =>
      prevItems.map((item) =>
        item.itemId === itemId
          ? {
            ...item,
            selected: !item.selected,
          }
          : item
      )
    );
  };

  // --------------------------------------------------
  // Select / deselect all items
  // --------------------------------------------------
  const handleSelectAll = (checked) => {
    setTableItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        selected: item.hasStock ? false : checked,
      }))
    );
  };

  // --------------------------------------------------
  // Handle quantity and rate inputs
  // --------------------------------------------------
  const handleInputChange = (itemId, field, value) => {
    if (value !== "" && Number(value) < 0) {
      return;
    }

    setTableItems((prevItems) =>
      prevItems.map((item) =>
        item.itemId === itemId
          ? {
            ...item,
            [field]: value,
          }
          : item
      )
    );
  };

  // --------------------------------------------------
  // Selected items
  // --------------------------------------------------
  const selectedItems = tableItems.filter(
    (item) => item.selected && !item.hasStock
  );

  // --------------------------------------------------
  // Calculate total amount
  // --------------------------------------------------
  const totalAmount = selectedItems.reduce((total, item) => {
    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;

    return total + quantity * rate;
  }, 0);

  // --------------------------------------------------
  // Submit selected items to backend
  // --------------------------------------------------
  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!selectedFactory) {
      setError("Please select a factory.");
      return;
    }

    if (selectedItems.length === 0) {
      setError("Please select at least one item.");
      return;
    }

    // Validate selected items
    for (const item of selectedItems) {
      if (!item.quantity || Number(item.quantity) <= 0) {
        setError(`Enter a valid quantity for ${item.itemName}.`);
        return;
      }

      if (
        item.rate === "" ||
        !Number.isFinite(Number(item.rate)) ||
        Number(item.rate) < 0
      ) {
        setError(`Enter a valid rate for ${item.itemName}.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      // Call existing API for each selected item
      // console.log(selectedItems);return
      const results = await Promise.all(
        selectedItems.map((item) =>
          api.post("/inventory/stock-in", {
            factoryId: selectedFactory.value,
            itemId: item.itemId,
            quantity: Number(item.quantity),
            rate: Number(item.rate),
          })
        )
      );

      setSuccess(
        `${results.length} opening stock items saved successfully.`
      );

      // Remove successfully submitted items
// Keep submitted items visible and disable them
setTableItems((prevItems) =>
    prevItems.map((item) => {
        const submittedItem = selectedItems.find(
            (selected) => selected.itemId === item.itemId
        );

        if (!submittedItem) {
            return item;
        }

        return {
            ...item,
            hasStock: true,
            selected: false,
            quantity: submittedItem.quantity,
            rate: submittedItem.rate,
            amount:
                Number(submittedItem.quantity) *
                Number(submittedItem.rate),
        };
    })
);
    } catch (error) {
      console.error("Error saving opening stock:", error);

      setError(
        error.response?.data?.message ||
        "Failed to save opening stock items."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --------------------------------------------------
  // React Select styles
  // --------------------------------------------------
  const customStyles = {
    control: (base) => ({
      ...base,
      borderColor: "#d1d5db",
      boxShadow: "none",
      minHeight: "40px",
      "&:hover": {
        borderColor: "#8b5cf6",
      },
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),

    menu: (base) => ({
      ...base,
      zIndex: 99999,
    }),
  };

  const allSelected =
    tableItems.length > 0 &&
    tableItems.every((item) => item.selected);

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div className="w-[95%] mx-auto pb-8">
      {/* Header */}
      <div className="h-10 px-3 mt-5 flex justify-between items-center text-violet-500 font-bold rounded-md bg-white shadow mb-2">
        <div className="text-lg">Opening Stock Form</div>
      </div>

      {/* Filters */}
      <div className="p-6 mt-4 w-full bg-white shadow-md rounded-lg">
        <div className="flex flex-wrap items-end gap-5">
          {/* Factory */}
          <div className="flex flex-col w-full sm:w-[220px]">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <label className="text-gray-700 font-medium">Opening Date:</label>
              <span className="text-sm text-violet-500 font-bold">
                {selectedFactory?.inventoryStartDate
                  ? new Date(selectedFactory.inventoryStartDate).toLocaleDateString("en-GB")
                  : ""}
              </span>
            </div>
            <label className="mb-1 text-gray-700 font-medium">
              Factory Location <span className="text-red-500">*</span>
            </label>

            <Select
              options={facLoc}
              value={selectedFactory}
              onChange={setSelectedFactory}
              placeholder="Select Factory"
              isSearchable
              isClearable
              styles={customStyles}
              menuPortalTarget={
                typeof document !== "undefined"
                  ? document.body
                  : null
              }
              menuPosition="fixed"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col w-full sm:w-[220px]">
            <label className="mb-1 text-gray-700 font-medium">
              Item Category <span className="text-red-500">*</span>
            </label>

            <Select
              options={itemGroups}
              value={selectedGroup}
              onChange={setSelectedGroup}
              placeholder="Select Category"
              isSearchable
              isClearable
              styles={customStyles}
              menuPortalTarget={
                typeof document !== "undefined"
                  ? document.body
                  : null
              }
              menuPosition="fixed"
            />
          </div>

          {/* Search */}
          <div>
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="bg-violet-500 text-white px-6 py-2.5 rounded-md hover:bg-violet-600 disabled:opacity-50 transition"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="mt-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-700">
          {success}
        </div>
      )}

      {/* Table */}
      {tableItems.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {/* Table Header */}
          <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                Item Details
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Total Items: {tableItems.length}{" "}
                <span className="mx-1 text-gray-300">|</span>
                Selected: {selectedItems.length}
              </p>
            </div>

            {/* Selected Amount */}
            <div className="w-full rounded-lg border border-violet-100 bg-violet-50 px-4 py-3 sm:w-auto">
              <p className="text-xs font-medium text-violet-700">
                Selected Amount
              </p>

              <p className="mt-1 text-lg font-bold text-violet-700 sm:text-xl">
                ₹
                {totalAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>

          {/* Responsive Table */}
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[850px] border-collapse text-left text-sm">
              {/* Table Head */}
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="w-14 px-4 py-4 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) =>
                        handleSelectAll(e.target.checked)
                      }
                      aria-label="Select all items"
                      className="h-4 w-4 cursor-pointer accent-violet-600"
                    />
                  </th>

                  <th className="whitespace-nowrap px-4 py-4">
                    S.No.
                  </th>

                  <th className="min-w-[220px] px-4 py-4">
                    Item Name
                  </th>

                  <th className="min-w-[170px] px-4 py-4">
                    Quantity
                  </th>

                  <th className="min-w-[170px] px-4 py-4">
                    Rate (₹)
                  </th>

                  <th className="min-w-[150px] px-4 py-4 text-right">
                    Amount (₹)
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-gray-100">
                {tableItems.map((item, index) => {
                  const quantity = Number(item.quantity) || 0;
const rate = Number(item.rate) || 0;

const amount = item.hasStock
    ? Number(item.amount) || 0
    : quantity * rate;

                  return (
                    <tr
                      key={item.itemId}
                      className={`transition-colors ${item.hasStock
                          ? "bg-gray-50"
                          : item.selected
                            ? "bg-violet-50"
                            : "hover:bg-gray-50"
                        }`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          disabled={item.hasStock}
                          onChange={() =>
                            handleCheckboxChange(item.itemId)
                          }
                          aria-label={`Select ${item.itemName}`}
                          className="h-4 w-4 accent-violet-600 disabled:cursor-not-allowed"
                        />
                      </td>

                      {/* Serial Number */}
                      <td className="whitespace-nowrap px-4 py-4 text-gray-500">
                        {index + 1}
                      </td>

                      {/* Item Name */}
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-800">
                          {item.itemName}
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-red-500">
                             {item.unit || "—"}
                          </span>

                        </div>
                      </td>

                      {/* Quantity */}
                      <td className="px-4 py-3">
                      <input
    type="number"
    min="0"
    step="0.001"
    value={item.quantity}
    disabled={item.hasStock}
    onChange={(e) =>
        handleInputChange(
            item.itemId,
            "quantity",
            e.target.value
        )
    }
    placeholder="Enter quantity"
    className={`w-full rounded-lg border px-3 py-2.5 outline-none ${
        item.hasStock
            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500"
            : "border-gray-300 bg-white"
    }`}
/>
                      </td>

                      {/* Rate */}
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          disabled={item.hasStock}
                          onChange={(e) =>
                            handleInputChange(
                              item.itemId,
                              "rate",
                              e.target.value
                            )
                          }
                          placeholder="Enter rate"
                          aria-label={`Rate for ${item.itemName}`}
                          className={`w-full rounded-lg border px-3 py-2.5 outline-none transition focus:ring-2 focus:ring-violet-200 ${item.hasStock
                              ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500"
                              : "border-gray-300 bg-white focus:border-violet-500"
                            }`}
                        />
                      </td>

                      {/* Amount */}
                      <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-gray-700">
                        {amount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Table Footer */}
              <tfoot className="border-t border-gray-200 bg-gray-50">
                <tr>
                  <td
                    colSpan={5}
                    className="whitespace-nowrap px-4 py-4 text-right font-semibold text-gray-700"
                  >
                    Selected Total Amount:
                  </td>

                  <td className="whitespace-nowrap px-4 py-4 text-right font-bold text-violet-700">
                    ₹
                    {totalAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer / Submit */}
          <div className="flex flex-col gap-3 border-t border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <p className="text-sm text-gray-500">
              {selectedItems.length} item(s) selected
            </p>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loading ||
                selectedItems.length === 0
              }
              className="inline-flex w-full items-center justify-center rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {submitting
                ? "Saving..."
                : `Save Opening Stock (${selectedItems.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpeningStock;